import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { DateRangePicker } from "react-date-range";
import { MonthPicker } from "@mantine/dates";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { scaleSequential } from "d3-scale";
import { interpolateRgb } from "d3-interpolate";
import {
  ComposableMap,
  Geographies,
  Geography,
  Sphere,
  Graticule,
  ZoomableGroup,
} from "react-simple-maps";

import { END_POINTS } from "../../../services/endpoint/endPoint";
import ApiCallServices from "../../../services/ApiCallServices";
import { mapping } from "./mapping";
import CountryHeatmapDialog from "./CountryHeatmapDialog";

const geoUrl = "/features.json";

const MapChart = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const currentDate = new Date();
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  const firstDayOfYear = new Date(currentDate.getFullYear(), 0, 1);

  const [data, setData] = useState([]);
  const [allDeals, setAllDeals] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [maxRevenue, setMaxRevenue] = useState(100_000_000_000);
  const INDIA_ISO3 = "IND";
  const INDIA_CENTER = [80, 22];
  const INDIA_ZOOM = 3.5;
  const [zoom, setZoom] = useState(INDIA_ZOOM);
  const [center, setCenter] = useState(INDIA_CENTER);

  const focusIndia = () => {
    setCenter(INDIA_CENTER);
    setZoom(INDIA_ZOOM);
  };

  const formatCurrency = (value) => {
    if (!value) return "$0";
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
  };

  const [durationType, setDurationType] = useState("daily");
  const [dateRange, setDateRange] = useState({
    startDate: firstDayOfMonth,
    endDate: currentDate,
    key: "selection",
  });
  const [monthRange, setMonthRange] = useState([firstDayOfYear, currentDate]);
  const [applyDateRange, setApplyDateRange] = useState(dateRange);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pickerType, setPickerType] = useState(null);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const applyDateFilter = () => {
    if (durationType === "monthly") {
      const startMonth = new Date(monthRange[0]);
      const endMonth = monthRange[1] ? new Date(monthRange[1]) : startMonth;
      setApplyDateRange({
        startDate: new Date(startMonth.getFullYear(), startMonth.getMonth(), 1),
        endDate: new Date(endMonth.getFullYear(), endMonth.getMonth() + 1, 0),
        key: "selection",
      });
    } else {
      setApplyDateRange(dateRange);
    }
  };

  const normalizeCountryName = (name) =>
    name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/[\(\)\.,']/g, "");

  const getISO3Code = (countryName) => {
    if (!countryName) return null;

    const normalized = normalizeCountryName(countryName);
    if (/^[a-z]{3}$/.test(normalized)) {
      return normalized.toUpperCase();
    }

    const exact = mapping[countryName];
    if (exact) return exact;

    const exactMatch = Object.entries(mapping).find(
      ([name, iso]) =>
        normalizeCountryName(name) === normalized ||
        normalizeCountryName(iso) === normalized
    );
    if (exactMatch) return exactMatch[1];

    return null;
  };

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const res = await ApiCallServices.create(END_POINTS.heatMapData, {
          startDate: applyDateRange?.startDate?.toISOString().split("T")[0],
          endDate: applyDateRange?.endDate?.toISOString().split("T")[0],
        });
        if (res.status === 200) {
          const rawDeals = res.data.data || [];
          setAllDeals(rawDeals);

          const aggregated = rawDeals.reduce((acc, item) => {
            const country = item.country || "";
            const iso3 = getISO3Code(country);
            const key = iso3 || normalizeCountryName(country);
            if (!acc[key])
              acc[key] = { country, iso3, imp: 0, revenueSorting: 0 };

            acc[key].imp += item.imp
              ? parseInt(String(item.imp).replace(/,/g, ""), 10)
              : 0;
            acc[key].revenueSorting += item.revenueSorting
              ? parseFloat(item.revenueSorting)
              : 0;
            return acc;
          }, {});

          const formatted = Object.values(aggregated);
          setMaxRevenue(
            Math.max(...formatted.map((d) => d.revenueSorting / 10 || 0)) ||
              100_000_000_000
          );
          setData(formatted);
        }
      } catch (error) {
        console.error("Error fetching deal list:", error);
      }
    };
    fetchDeals();
  }, [applyDateRange]);

  const handleCountryClick = (geo) => {
    console.log(geo);
    const countryData = data.find((d) => d.iso3 === geo.id);
    if (!countryData) return;

    const deals = allDeals
      .filter((d) => getISO3Code(d.country) === geo.id)
      .sort((a, b) => (b.revenueSorting || 0) - (a.revenueSorting || 0))
      .slice(0, 20)
      .sort((a, b) => (a.id || 0) - (b.id || 0))
      .map((d) => ({
        ...d,
        supply_partner_name: d.supply_partner_name || "",
        brand_name: d.brand_name || "",
        agency_name: d.agency_name || "",
        demand_partner_name: d.demand_partner_name || "",
      }));
    console.log(allDeals);
    console.log(deals);
    setSelectedCountry({ name: geo.properties.name, iso: geo.id, deals });
  };

  const handleOpenPopover = (event, type) => {
    setAnchorEl(event.currentTarget);
    setPickerType(type);
  };
  const handleClosePopover = () => {
    setAnchorEl(null);
    setPickerType(null);
  };

  return (
    <>
      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 10,
              height: 34,
              borderRadius: 999,
              background: "linear-gradient(180deg, #38BDF8, #DC2626)",
            }}
          />
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: "#0049aa",
                fontWeight: 800,
                letterSpacing: "-0.01em",
                fontFamily: "'Gilroy-Bold', 'Gilroy', sans-serif",
              }}
            >
              Global Revenue Heatmap
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#6b7280", fontFamily: "'Gilroy-Medium', 'Gilroy', sans-serif" }}
            >
              Revenue by country for the selected period, centered on India
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={durationType}
              onChange={(e) => setDurationType(e.target.value)}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>

          {durationType === "monthly" ? (
            <>
              <TextField
                value={
                  monthRange[0] && monthRange[1]
                    ? `${format(monthRange[0], "MMM yyyy")} - ${format(
                        monthRange[1],
                        "MMM yyyy"
                      )}`
                    : monthRange[0]
                    ? `${format(monthRange[0], "MMM yyyy")} - ${format(
                        monthRange[0],
                        "MMM yyyy"
                      )}`
                    : ""
                }
                onClick={(e) => handleOpenPopover(e, "monthly")}
                size="small"
                sx={{ width: 250 }}
                InputProps={{ readOnly: true }}
              />
              <Popover
                open={pickerType === "monthly"}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <Box sx={{ p: 2 }}>
                  <MonthPicker
                    value={monthRange[0]}
                    onChange={(val) => {
                      if (!monthRange[0] || (monthRange[0] && monthRange[1])) {
                        setMonthRange([val, null]); // start=end same
                      } else {
                        setMonthRange([monthRange[0], val]);
                      }
                    }}
                    maxDate={new Date()}
                  />

                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Click first month, then second month for range
                  </Typography>

                  <Box
                    sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        applyDateFilter();
                        handleClosePopover();
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </Popover>
            </>
          ) : (
            <>
              <TextField
                value={`${format(dateRange.startDate, "yyyy-MM-dd")} - ${format(
                  dateRange.endDate,
                  "yyyy-MM-dd"
                )}`}
                onClick={(e) => handleOpenPopover(e, "daily")}
                size="small"
                sx={{ width: 250 }}
                InputProps={{ readOnly: true }}
              />
              <Popover
                open={pickerType === "daily"}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <Box sx={{ p: 2 }}>
                  <DateRangePicker
                    ranges={[dateRange]}
                    onChange={(ranges) => setDateRange(ranges.selection)}
                    maxDate={new Date()}
                  />

                  <Box
                    sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        applyDateFilter();
                        handleClosePopover();
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              </Popover>
            </>
          )}
        </Box>
      </Box>

      {/* Map */}
      <Box
        sx={{
          width: "100%",
          height: isSmallScreen ? "calc(100vh - 220px)" : "calc(100vh - 180px)",
          background: "linear-gradient(160deg, #0b1220 0%, #131b2e 100%)",
          p: 2,
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 20px 50px rgba(2,6,23,0.45)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 14,
            left: 14,
            display: "flex",
            flexDirection: "column",
            bgcolor: "rgba(255,255,255,0.08)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            overflow: "hidden",
            zIndex: 10,
          }}
        >
          <IconButton
            size="small"
            sx={{ borderRadius: 0, color: "#e5e7eb" }}
            onClick={() => setZoom((z) => Math.min(z * 1.5, 8))}
          >
            <AddIcon />
          </IconButton>
          <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.12)" }} />
          <IconButton
            size="small"
            sx={{ borderRadius: 0, color: "#e5e7eb" }}
            onClick={() => setZoom((z) => Math.max(z / 1.5, 1))}
          >
            <RemoveIcon />
          </IconButton>
        </Box>

        <Button
          size="small"
          variant="contained"
          onClick={focusIndia}
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            zIndex: 10,
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            color: "#0b1220",
            background: "linear-gradient(135deg, #7dd3fc, #38BDF8)",
            boxShadow: "0 8px 24px rgba(56,189,248,0.35)",
            "&:hover": { background: "linear-gradient(135deg, #38BDF8, #0ea5e9)" },
          }}
        >
          🇮🇳 Focus India
        </Button>

        {/* Legend: explains the revenue color scale and India's highlight */}
        <Box
          sx={{
            position: "absolute",
            bottom: 14,
            left: 14,
            zIndex: 10,
            bgcolor: "rgba(255,255,255,0.08)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            px: 1.5,
            py: 1,
            fontSize: 12,
            minWidth: 170,
          }}
        >
          <Box sx={{ fontWeight: 700, mb: 0.75, color: "#f1f5f9" }}>
            Revenue
          </Box>
          <Box
            sx={{
              height: 8,
              borderRadius: 999,
              background: "linear-gradient(to right, #FDBA74, #DC2626)",
              mb: 0.5,
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", color: "#94a3b8", fontSize: 11 }}>
            <span>Low</span>
            <span>High ({formatCurrency(maxRevenue)})</span>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: 0.5,
                bgcolor: "#1c3a63",
                border: "1.5px solid #38BDF8",
                boxShadow: "0 0 6px rgba(56,189,248,0.6)",
              }}
            />
            <span style={{ color: "#94a3b8" }}>India</span>
          </Box>
        </Box>

        {hoverInfo && (
          <Box
            sx={{
              position: "absolute",
              pointerEvents: "none",
              top: tooltipPos.y,
              left: tooltipPos.x,
              transform: "translate(-50%, -120%)",
              zIndex: 20,
              bgcolor: "rgba(17,24,39,0.92)",
              color: "#fff",
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              fontSize: 12,
              whiteSpace: "nowrap",
              boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
            }}
          >
            <Box sx={{ fontWeight: 700, mb: hoverInfo.hasData ? 0.25 : 0 }}>
              {hoverInfo.isIndia ? "🇮🇳 " : ""}
              {hoverInfo.name}
            </Box>
            {hoverInfo.hasData && (
              <Box sx={{ color: "#FDBA74" }}>
                Revenue: {formatCurrency(hoverInfo.revenue)}
              </Box>
            )}
          </Box>
        )}
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            center: [80, 22],
            scale: isSmallScreen ? 180 : 260,
            precision: 0.1,
          }}
          style={{ width: "100%", height: "100%" }}
        >
          <ZoomableGroup
            center={center}
            zoom={zoom}
            minZoom={1}
            maxZoom={8}
            onMoveEnd={({ coordinates, zoom }) => {
              setCenter(coordinates);
              setZoom(zoom);
            }}
          >
            <Sphere fill="#0b1220" stroke="rgba(255,255,255,0.06)" strokeWidth={0.5} />
            <Graticule stroke="rgba(255,255,255,0.04)" strokeWidth={0.5} />
            <Geographies geography={geoUrl}>
              {({ geographies }) => {
                const colorScale = scaleSequential(
                  interpolateRgb("#FDBA74", "#DC2626")
                )
                  .domain([0, maxRevenue])
                  .clamp(true);

                return geographies.map((geo) => {
                  const countryData = data.find((d) => d.iso3 === geo.id);
                  const isIndia = geo.id === INDIA_ISO3;
                  const fillColor = countryData
                    ? colorScale(countryData.revenueSorting || 0)
                    : isIndia
                    ? "#1c3a63"
                    : "#1a2237";

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fillColor}
                      stroke={isIndia ? "#38BDF8" : "rgba(255,255,255,0.08)"}
                      strokeWidth={isIndia ? 1.75 / zoom : 0.4 / zoom}
                      title={geo.properties.name}
                      aria-label={geo.properties.name}
                      style={{
                        default: {
                          outline: "none",
                          filter: isIndia
                            ? "drop-shadow(0 0 6px rgba(56,189,248,0.65))"
                            : "none",
                          transition: "fill 0.2s ease",
                        },
                        hover: countryData
                          ? {
                              fill: "#FCD34D",
                              outline: "none",
                              cursor: "pointer",
                            }
                          : {
                              fill: isIndia ? "#274b7d" : "#232c46",
                              outline: "none",
                              cursor: "not-allowed",
                            },
                        pressed: { outline: "none" },
                      }}
                      onMouseEnter={(event) => {
                        setHoverInfo({
                          name: geo.properties.name,
                          isIndia,
                          hasData: !!countryData,
                          revenue: countryData?.revenueSorting || 0,
                        });
                        setTooltipPos({
                          x: event.clientX,
                          y: event.clientY,
                        });
                      }}
                      onMouseMove={(event) => {
                        setTooltipPos({
                          x: event.clientX,
                          y: event.clientY,
                        });
                      }}
                      onMouseLeave={() => setHoverInfo(null)}
                      onClick={() => countryData && handleCountryClick(geo)}
                    />
                  );
                });
              }}
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </Box>

      {selectedCountry && (
        <CountryHeatmapDialog
          open={!!selectedCountry}
          onClose={() => setSelectedCountry(null)}
          countryName={selectedCountry.name}
          countryDeals={selectedCountry.deals}
        />
      )}
    </>
  );
};

export default MapChart;
